import React from 'react';
import {
  Car,
  UserCircle,
  Clock,
  IndianRupee,
  MapPin,
  Star,
  Share2,
  Timer,
  Globe,
  Shield,
  FileText,
  Lock,
  Headphones,
} from 'lucide-react';
import { useTestimonials } from '@/hooks/useTestimonials';
import { StarText } from '@/components/admin/TestimonialStars';
import { FALLBACK_FEATURED } from '@/components/admin/testimonialUtils';

const items = [
  {
    icon: Car,
    title: 'AC Clean Cars',
    desc: 'Sanitised, odour-free, well-maintained vehicles.',
    proof: '100% AC fleet · Cleaned after every single trip',
  },
  {
    icon: UserCircle,
    title: 'Expert Drivers',
    desc: 'Verified, trained & courteous professionals.',
    proof: '5+ yrs avg experience · Background checked',
  },
  {
    icon: Clock,
    title: '24/7 Available',
    desc: 'Round the clock service, any city, any time.',
    proof: 'Response under 2 mins · Early morning & late night',
  },
  {
    icon: IndianRupee,
    title: 'No Hidden Charges',
    desc: 'Transparent billing. Toll & taxes included.',
    proof: 'Quoted fare = Final fare. Always.',
  },
  {
    icon: MapPin,
    title: 'Doorstep Pickup',
    desc: 'Driver comes to your location on time.',
    proof: 'Driver arrives 10 mins early · Helps with luggage',
  },
  {
    icon: Star,
    title: 'Trusted Service',
    desc: '10,000+ happy rides across India.',
    proof: '4.9★ avg Google rating · Since 2024',
  },
  {
    icon: Share2,
    title: 'Driver Details Shared',
    desc: 'Driver name, number & vehicle shared on WhatsApp before pickup.',
    proof: 'No last-minute surprises · Always informed',
  },
  {
    icon: Timer,
    title: 'Free Waiting Time',
    desc: 'Driver waits up to 30 minutes at pickup — no extra charge.',
    proof: 'Your schedule, our patience',
  },
  {
    icon: Globe,
    title: 'Pan-India Routes',
    desc: 'From Gujarat to any state. One driver, one journey.',
    proof: 'Rajasthan · Maharashtra · MP · Delhi & beyond',
  },
];

const trustItems = [
  { icon: Shield, text: 'GST Invoice Available' },
  { icon: FileText, text: 'Written Fare Quote on WhatsApp' },
  { icon: Lock, text: 'Safe & Insured Travel' },
  { icon: Headphones, text: '24/7 Customer Support' },
];

const FeaturedQuote = ({ testimonial }) => {
  const routeLine = [testimonial.route, testimonial.travel_month].filter(Boolean).join(' · ');
  return (
    <div
      className="mx-auto"
      style={{
        background: 'white',
        borderLeft: '4px solid #E8651A',
        borderRadius: '0 12px 12px 0',
        padding: '24px 32px',
        marginTop: '24px',
        maxWidth: '700px',
      }}
    >
      <span
        className="font-display block"
        style={{ fontSize: '64px', color: '#E8651A', opacity: 0.3, lineHeight: 0, marginBottom: '-16px' }}
        aria-hidden
      >
        &ldquo;
      </span>
      <p
        className="font-display italic text-[#1B2D5E]"
        style={{ fontSize: '20px', lineHeight: 1.6 }}
      >
        {testimonial.review_text}
      </p>
      <p className="font-body font-semibold text-[#333] mt-3" style={{ fontSize: '13px' }}>
        — {testimonial.customer_name}
      </p>
      {routeLine && (
        <p className="font-body text-[#888]" style={{ fontSize: '12px' }}>
          {routeLine}
        </p>
      )}
      <div className="mt-1">
        <StarText rating={testimonial.rating || 5} />
      </div>
    </div>
  );
};

const AtithiWhyChooseSection = () => {
  const { testimonials, loading, error } = useTestimonials({ featured: true, active: true, limit: 1 });
  const featured = testimonials[0] || null;
  const quoteToShow = error ? FALLBACK_FEATURED : featured;

  return (
    <section className="section-outer section-standard bg-atithi-bg-muted" data-aos="fade-up">
      <div className="section-inner section-inner--follow-about">
        <div className="mb-10">
          <p className="section-label">WHY CHOOSE US</p>
          <h2 className="section-heading mt-2">Why Choose Atithi?</h2>
        </div>

        <div className="grid-cards-3">
          {items.map(({ icon: Icon, title, desc, proof }) => (
            <div
              key={title}
              className="bg-white border border-gray-100 rounded-[14px] p-7 transition-all duration-200 hover:border-[#E8651A]/30 hover:shadow-md"
            >
              <Icon size={32} className="text-[#E8651A] mb-4" strokeWidth={1.8} />
              <h3 className="font-body font-semibold text-[#1B2D5E] mb-2" style={{ fontSize: '15px' }}>
                {title}
              </h3>
              <p className="font-body text-[#666] leading-relaxed" style={{ fontSize: '13px', lineHeight: 1.6 }}>
                {desc}
              </p>
              <p
                className="font-body mt-3"
                style={{ fontSize: '11px', color: '#E8651A', fontWeight: 500, lineHeight: 1.5 }}
              >
                {proof}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-[#0D1B3E] rounded-2xl px-8 py-4 mt-10 flex flex-wrap items-center justify-around gap-4">
          {trustItems.map(({ icon: Icon, text }, i) => (
            <React.Fragment key={text}>
              {i > 0 && (
                <span className="hidden md:inline-block w-px h-6 bg-white/10 shrink-0" />
              )}
              <div className="flex items-center gap-2.5 font-body text-sm text-white/80 font-medium">
                <Icon size={24} className="text-[#E8651A] shrink-0" strokeWidth={1.8} />
                <span>{text}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {!loading && quoteToShow && <FeaturedQuote testimonial={quoteToShow} />}
      </div>
    </section>
  );
};

export default AtithiWhyChooseSection;
